import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Smartphone, Sparkles, Monitor, Download, Zap, Database, Globe } from "lucide-react";

export default function CrmProDetail() {
  const navigate = useNavigate();

  const playStoreUrl = "https://play.google.com/store/apps/details?id=com.crm.pro.app";
  const demoUrl = "https://crmdemo.hickter.com/admin/authentication";

  const features = [
    {
      title: "Lead Pipeline & Opportunity Tracking",
      desc: "Automated lead capture, kanban stage tracking, and deal conversion workflows for sales teams.",
      icon: <Sparkles size={20} className="text-blue-600 dark:text-cyan-400" />
    },
    {
      title: "Project & Milestone Invoicing Engine",
      desc: "Instant professional invoice generation, multi-currency billing, tax calculations, and payment tracking.",
      icon: <Database size={20} className="text-indigo-600 dark:text-indigo-400" />
    },
    {
      title: "Communication & Client Activity Log",
      desc: "Automated email logs, call records, WhatsApp notifications, and customer interaction timelines.",
      icon: <Globe size={20} className="text-emerald-600 dark:text-emerald-400" />
    },
    {
      title: "Executive Analytics & Sales Forecasts",
      desc: "Real-time sales performance dashboards, revenue forecasting, and team productivity reports.",
      icon: <Zap size={20} className="text-amber-600 dark:text-amber-400" />
    }
  ];

  const techStack = ["React", "PHP", "TailwindCSS", "MySQL", "Perfex CRM", "REST API"];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-100 font-sans pb-32 pt-6 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-5xl mx-auto space-y-8">
        
        {/* TOP BACK BUTTON */}
        <div>
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-cyan-400 font-bold text-xs transition-all shadow-sm cursor-pointer group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span>Back to Products</span>
          </button>
        </div>

        {/* HERO CARD */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xl space-y-8 relative overflow-hidden">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 p-3 shrink-0 shadow-md flex items-center justify-center">
                <img 
                  src="https://play-lh.googleusercontent.com/gY20VDzqDBNx5yFFULUlAxipr7GLB59V2RHeyZ2Cyj40ffAkW30FjF2pmE8DqduIyHxxZY90wfMYrB2s2HbNag=w240-h480-rw" 
                  alt="CRMPro Logo" 
                  className="w-full h-full object-contain rounded-xl"
                />
              </div>
              <div className="space-y-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Google PlayStore & Web Suite Live
                </div>
                <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                  CRMPro By Hickter Perfex CRM
                </h1>
                <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium">
                  Smart Customer Relationship & Sales Management Platform
                </p>
              </div>
            </div>

            {/* ACTION LINKS */}
            <div className="flex items-center gap-3 w-full md:w-auto">
              <a
                href={playStoreUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-primary-blue flex-1 md:flex-initial py-3 px-5 text-xs font-bold rounded-xl flex items-center justify-center gap-2 !text-white shadow-lg shadow-blue-500/20"
              >
                <Smartphone size={16} className="!text-white" />
                <span className="!text-white">PlayStore</span>
                <ExternalLink size={13} className="!text-white" />
              </a>

              <a
                href={demoUrl}
                target="_blank"
                rel="noreferrer"
                className="flex-1 md:flex-initial py-3 px-5 text-xs font-extrabold rounded-xl bg-black dark:bg-black text-white !text-white border border-slate-800 flex items-center justify-center gap-2 shadow-md hover:bg-slate-900 transition-colors"
              >
                <Monitor size={16} className="!text-white" />
                <span className="!text-white font-extrabold">Web Demo</span>
              </a>
            </div>
          </div>

          <p className="text-slate-700 dark:text-slate-300 text-sm sm:text-base leading-relaxed">
            CRMPro is an all-in-one business operation and CRM platform built for sales acceleration, lead management, project invoicing, and customer engagement.
          </p>

          {/* QUICK STATS */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Lead Pipeline</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">Kanban Stages</p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Invoicing Engine</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">Automated</p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Client Portal</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">Role-Based</p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Platform Access</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">Android & Web</p>
            </div>
          </div>
        </div>

        {/* CORE CAPABILITIES GRID */}
        <div className="space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Core Capabilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((f, i) => (
              <div 
                key={i}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow space-y-3"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800">
                    {f.icon}
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white text-base">{f.title}</h3>
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed pl-1">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* TECH STACK BADGES */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-sm">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">Technology Stack & Architecture</h2>
          <div className="flex flex-wrap gap-2">
            {techStack.map((tech, i) => (
              <span 
                key={i}
                className="px-3.5 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>

      </div>

      {/* FIXED BOTTOM DOWNLOAD & ACCESS BAR */}
      <div className="fixed bottom-0 left-0 right-0 z-[110] bg-white/95 dark:bg-[#030712]/95 backdrop-blur-xl border-t border-slate-200 dark:border-slate-800 p-3 sm:p-4 shadow-2xl">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 sm:gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 p-1.5 shrink-0 hidden sm:flex items-center justify-center">
              <img 
                src="https://play-lh.googleusercontent.com/gY20VDzqDBNx5yFFULUlAxipr7GLB59V2RHeyZ2Cyj40ffAkW30FjF2pmE8DqduIyHxxZY90wfMYrB2s2HbNag=w240-h480-rw" 
                alt="CRMPro Logo" 
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
            <div className="text-left">
              <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-white truncate">CRMPro By Hickter</h4>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium hidden sm:block">Official Google PlayStore & Web Suite</p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto shrink-0">
            <a
              href={playStoreUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 sm:flex-initial py-2.5 px-5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-500 hover:to-indigo-600 text-white font-extrabold text-xs flex items-center justify-center gap-2 transition-all shadow-md shadow-blue-500/25 !text-white"
            >
              <Download size={15} className="!text-white" />
              <span className="!text-white font-black">Get App</span>
            </a>

            <a
              href={demoUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 sm:flex-initial py-2.5 px-4 rounded-xl bg-black dark:bg-black text-white !text-white border border-slate-800 text-xs font-extrabold flex items-center justify-center gap-2 shadow-sm hover:bg-slate-900 transition-colors"
            >
              <Monitor size={15} className="!text-white" />
              <span className="!text-white font-extrabold">Web Login</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
